import React, { useState, useMemo } from 'react';
import satisfactionApi from '../services/satisfaction-campagne';

// Aperçu du lien : longueur réaliste (base Retours Prospects + token hex 48) pour
// que le compteur 160 soit honnête. Le vrai lien unique est créé à l'envoi.
const LIEN_EXEMPLE = 'https://avis.autodom97.fr/0123456789abcdef0123456789abcdef0123456789abcdef';
const sansAccents = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');

// Même substitution que le backend (import-satisfaction.rendre), pour l'aperçu live.
function rendre(tpl, c, lien) {
  const v = {
    prenom: c?.prenom || '', nom: c?.nom || '', civilite: c?.civilite || '',
    concession: c?.concession || '', marque: c?.marque || '', modele: c?.modele || '', lien,
  };
  return String(tpl || '').replace(/\{\{(\w+)\}\}/g, (_, k) => (k in v ? v[k] : `{{${k}}}`));
}

function raison(c) {
  if (c.envoyable) {
    const via = c.canalSms && c.canalEmail ? 'SMS + email' : (c.canalSms ? 'SMS' : 'Email');
    return { txt: via, cls: 'ok' };
  }
  if (c.canaux === 'email') return { txt: 'Sans email', cls: 'ko' };
  if (c.statut === 'mobile' && !c.concessionPeutEnvoyer) return { txt: 'Concession sans SMS', cls: 'warn' };
  if (c.statut === 'fixe') return { txt: 'Fixe (pas de SMS)', cls: 'ko' };
  if (c.statut === 'invalide') return { txt: 'N° invalide', cls: 'ko' };
  if (c.statut === 'vide') return { txt: 'Sans numéro', cls: 'ko' };
  return { txt: c.statut, cls: 'ko' };
}

export default function SatisfactionCampagne() {
  const [fichiers, setFichiers] = useState([]);
  const [type, setType] = useState('VN');
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState('');
  const [exclus, setExclus] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [confirmOuvert, setConfirmOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(null);

  const contacts = data?.contacts || [];
  const premierEnvoyable = useMemo(() => contacts.find((c) => c.envoyable), [contacts]);
  const apercu = rendre(template, premierEnvoyable, LIEN_EXEMPLE);
  const longueur = sansAccents(apercu).length;
  const aEnvoyer = useMemo(
    () => contacts.filter((c) => c.envoyable && !exclus.has(c.contactId)),
    [contacts, exclus]);
  const aSms = aEnvoyer.some((c) => c.canalSms);   // le contrôle 160 ne s'applique qu'au SMS

  const lancerAnalyse = async () => {
    if (!fichiers.length) { setErreur('Choisissez au moins un fichier.'); return; }
    setLoading(true); setErreur(''); setEnvoi(null);
    try {
      const { data: res } = await satisfactionApi.analyser(fichiers, type);
      setData(res);
      setTemplate(res.templateDefaut || '');
      setExclus(new Set());
    } catch (e) {
      setErreur(e.response?.data?.error || 'Échec de l\'analyse.');
    } finally { setLoading(false); }
  };

  const insererVar = (v) => setTemplate((t) => `${t}{{${v}}}`);
  const toggleExclu = (id) => setExclus((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const envoyer = async () => {
    setConfirmOuvert(false); setLoading(true); setErreur('');
    try {
      const { data: res } = await satisfactionApi.envoyer(aEnvoyer, template);
      setEnvoi(res);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Échec de l\'envoi.');
    } finally { setLoading(false); }
  };

  const s = data?.stats;

  return (
    <div className="satisfaction-campagne" style={{ padding: '4px 2px' }}>
      {/* 1. Dépôt */}
      <div className="card" style={{ padding: 16, marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>1 · Fichiers de campagne</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".csv,text/csv" multiple
            onChange={(e) => setFichiers(Array.from(e.target.files))} />
          <label>Type :&nbsp;
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="VN">VN</option>
              <option value="VO">VO</option>
              <option value="VD">VD</option>
              <option value="">Tous</option>
            </select>
          </label>
          <button onClick={lancerAnalyse} disabled={loading || !fichiers.length}>
            {loading ? 'Analyse…' : 'Analyser'}
          </button>
          {fichiers.length > 0 && <span style={{ color: '#64748b' }}>{fichiers.length} fichier(s)</span>}
        </div>
        {erreur && <p style={{ color: '#b91c1c', marginBottom: 0 }}>{erreur}</p>}
      </div>

      {data && (
        <>
          {/* Bandeau de synthèse */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi n={s.contacts} l="Clients uniques" />
            <Kpi n={contacts.filter((c) => c.envoyable).length} l="Joignables (SMS/email)" c="#16a34a" />
            <Kpi n={s.avecEmailValide} l="Avec e-mail valide" />
            <Kpi n={s.sansConsentement} l="SMS sans consentement" c="#b45309" />
          </div>

          {/* Récap par concession (avec l'alerte SMS non configuré) */}
          <div style={{ marginBottom: 16 }}>
            <b>Par concession :</b>{' '}
            {Object.entries(data.recap).map(([nom, r]) => (
              <span key={nom} style={{ marginRight: 12 }}>
                {nom} : <b>{r.envoyables}</b>/{r.mobiles}
                {!r.peutEnvoyer && r.mobiles > 0 && <span title="SMS non configuré, ou module Satisfaction désactivé pour cette concession" style={{ color: 'var(--color-warning)' }}> ⚠</span>}
                {!r.formulaireId && <span title="Aucun formulaire de source « csv » actif dans cette concession : le lien n'aurait aucun questionnaire à servir" style={{ color: 'var(--color-warning)' }}> ⚠ sans formulaire</span>}
              </span>
            ))}
          </div>

          {/* 2. Message SMS éditable */}
          <div className="card" style={{ padding: 16, marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <h3 style={{ marginTop: 0 }}>2 · Message SMS <small style={{ fontWeight: 400, color: '#64748b' }}>(le canal e-mail utilise un message standard avec le même lien)</small></h3>
            <div style={{ marginBottom: 8 }}>
              {data.variables.map((v) => (
                <button key={v} type="button" onClick={() => insererVar(v)}
                  style={{ marginRight: 6, marginBottom: 6, fontSize: 12, padding: '2px 8px', borderRadius: 12, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: 'pointer' }}>
                  {'{{'}{v}{'}}'}
                </button>
              ))}
            </div>
            <textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={3}
              style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', padding: 8 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: longueur > 160 ? '#b91c1c' : '#64748b' }}>
              <span>Aperçu ({premierEnvoyable ? `${premierEnvoyable.prenom} ${premierEnvoyable.nom}` : 'exemple'}) : <i>{apercu}</i></span>
              <b>{longueur}/160{longueur > 160 ? ' ⚠ trop long' : ''}</b>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
              Le lien affiché est un exemple ; à l'envoi, chaque client reçoit un lien unique vers le formulaire de satisfaction.
              Décompte sans accents (comme Mobbiz).
            </p>
          </div>

          {/* 3. Tableau de contrôle */}
          <div className="card" style={{ padding: 16, marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <h3 style={{ marginTop: 0 }}>3 · Tableau de contrôle — {aEnvoyer.length} envoi{aEnvoyer.length > 1 ? 's' : ''} (SMS/e-mail selon la concession)</h3>
            <div style={{ maxHeight: 420, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                    <th></th><th>Canal</th><th>Concession</th><th>Numéro</th><th>Email</th><th>Client</th><th>Véhicule</th><th>Consent.</th><th>Offres</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => {
                    const r = raison(c);
                    const col = { ok: '#16a34a', warn: '#b45309', ko: '#b91c1c' }[r.cls];
                    return (
                      <tr key={c.contactId} style={{ borderBottom: '1px solid #f1f5f9', opacity: exclus.has(c.contactId) ? 0.45 : 1 }}>
                        <td>{c.envoyable && <input type="checkbox" checked={!exclus.has(c.contactId)} onChange={() => toggleExclu(c.contactId)} />}</td>
                        <td style={{ color: col, fontWeight: 600 }}>{r.txt}</td>
                        <td>{c.concession}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.numero || c.numeroBrut || '—'}</td>
                        <td style={{ color: c.email && !c.emailValide ? '#b91c1c' : undefined, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.email}>{c.email || '—'}</td>
                        <td>{[c.civilite, c.prenom, c.nom].filter(Boolean).join(' ')}</td>
                        <td>{[c.marque, c.modele].filter(Boolean).join(' ')}</td>
                        <td>{c.consentPortable ? 'oui' : 'non'}</td>
                        <td>{c.nbOffres}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4. Envoi */}
          <div style={{ marginBottom: 24 }}>
            <button onClick={() => setConfirmOuvert(true)} disabled={loading || !aEnvoyer.length || (aSms && longueur > 160)}
              style={{ background: '#16a34a', color: '#fff', padding: '10px 18px', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
              Envoyer {aEnvoyer.length} message{aEnvoyer.length > 1 ? 's' : ''}
            </button>
            {aSms && longueur > 160 && <span style={{ color: '#b91c1c', marginLeft: 12 }}>SMS trop long, raccourcissez-le.</span>}
          </div>

          {envoi && (
            <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
              Envoi terminé : <b>{envoi.envoyes}</b>/{envoi.total} SMS partis.
              {envoi.envoyes < envoi.total && ` (${envoi.total - envoi.envoyes} en échec)`}
            </div>
          )}
        </>
      )}

      {/* Confirmation */}
      {confirmOuvert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setConfirmOuvert(false)}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Confirmer l'envoi</h3>
            <p>Vous allez envoyer <b>{aEnvoyer.length} message{aEnvoyer.length > 1 ? 's' : ''} réel{aEnvoyer.length > 1 ? 's' : ''}</b> (SMS et/ou e-mail selon la concession). Cette action est irréversible.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmOuvert(false)}>Annuler</button>
              <button onClick={envoyer} style={{ background: '#16a34a', color: '#fff', border: 0, borderRadius: 6, padding: '8px 16px' }}>Confirmer l'envoi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ n, l, c }) {
  return (
    <div style={{ padding: '10px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: c || '#0f172a' }}>{n}</div>
      <div style={{ fontSize: 12, color: '#64748b' }}>{l}</div>
    </div>
  );
}
